// src/components/PostarModal.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPost, editPost, getTimelineConfig } from "../services/timelineService";
import nomesBonitosCategoria from "../utils/nomesBonitosCategoria";
import obterNomeCategoria from "../utils/obterNomeCategoria";
import { categoriasPorModo } from "../services/estruturaCategorias";
import "./PostarModal.css";
import XPopup from "./XPopup";

const ICONS = {
  arrowDropdown: "https://i.imgur.com/Nppt817.png",
  arrowOverflow: "https://i.imgur.com/7B3fBYd.png",
};

const MODO_LABELS = {
  "oscar-indicados": "Indicados ao Oscar",
  "oscar-vencedores": "Vencedores do Oscar",
  "globodeouro-indicados": "Indicados ao Globo de Ouro",
  "globodeouro-vencedores": "Vencedores do Globo de Ouro",
  "sagawards-indicados": "Indicados ao SAG Awards",
  "sagawards-vencedores": "Vencedores do SAG Awards",
  "criticschoice-indicados": "Indicados ao Critics Choice",
  "criticschoice-vencedores": "Vencedores do Critics Choice",
  "dga-indicados": "Indicados ao DGA Awards",
  "dga-vencedores": "Vencedores do DGA Awards",
  "pga-indicados": "Indicados ao PGA Awards",
  "pga-vencedores": "Vencedores do PGA Awards",
  "bafta-indicados": "Indicados ao BAFTA",
  "bafta-vencedores": "Vencedores do BAFTA",
};

const PLACEHOLDER_IMG = "https://i.imgur.com/6MbQoCt.png";
const MAX_TEXT_CHARS = 1000;

const labelCategoria = (slug) => nomesBonitosCategoria?.[slug] || obterNomeCategoria(slug);
const isTrue = (v) => v === true || v === "true" || v === 1 || v === "1";
const isFalse = (v) => v === false || v === "false" || v === 0 || v === "0";
function classePos(n) { if (n === 1) return "pos-1"; if (n === 2) return "pos-2"; if (n === 3 || n === 4) return "pos-3-4"; return "pos-5-plus"; }

function getModosDisponiveis(snapByModo) {
  if (!snapByModo || typeof snapByModo !== "object") return [];
  const modos = Object.keys(snapByModo).filter((m) => categoriasPorModo?.[m]);
  return modos.filter((m) => {
    const catsUser = snapByModo[m] || {};
    return Object.values(catsUser).some((posicoes) =>
      Object.values(posicoes || {}).some((p) => p && typeof p === "object" && (p.titulo || "").trim() !== "")
    );
  });
}
function getCategoriasDisponiveisOrdenadas(modo, snapByModo) {
  if (!modo || !categoriasPorModo?.[modo]) return [];
  const ordem = categoriasPorModo[modo];
  const catsUser = snapByModo?.[modo] || {};
  return ordem.filter((slug) => {
    const posicoes = catsUser[slug];
    if (!posicoes) return false;
    return Object.values(posicoes).some((p) => p && typeof p === "object" && (p.titulo || "").trim() !== "");
  });
}

/* ===========================
   Spoiler Marker (edita textarea)
   =========================== */
function useSpoilerMarker(textRef, value, setValue) {
  const [marking, setMarking] = useState(false);

  function wrapSelection() {
    const ta = textRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    if (start === end) return;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    const already = /^\[spoiler\][\s\S]*\[\/spoiler\]$/.test(sel);
    const wrapped = already ? sel.replace(/^\[spoiler\]|\[\/spoiler\]$/g, "") : `[spoiler]${sel}[/spoiler]`;
    const next = before + wrapped + after;
    setValue(next);
    const caret = before.length + wrapped.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }

  function unwrapSelection() {
    const ta = textRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    if (start === end) return;
    const before = value.slice(0, start);
    let sel = value.slice(start, end);
    const after = value.slice(end);
    sel = sel.replace(/\[spoiler\]/gi, "").replace(/\[\/spoiler\]/gi, "");
    const next = before + sel + after;
    setValue(next);
    const caret = before.length + sel.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }

  useEffect(() => {
    function onKey(e) {
      if (!marking) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key.toLowerCase() === "m") {
        e.preventDefault();
        wrapSelection();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [marking, wrapSelection]);

  return {
    marking,
    start: () => setMarking(true),
    finish: () => setMarking(false),
    wrapSelection,
    unwrapSelection,
  };
}

function textHasInlineSpoiler(t = "") {
  return /\[spoiler\][\s\S]*?\[\/spoiler\]/i.test(String(t || ""));
}

export default function PostarModal({
  currentUser,
  snapByModo = {},
  onClose,
  onDone,
  editing,
}) {
  // FALLBACK DE EDIÇÃO
  const [editingFallback] = useState(() => {
    try {
      const s = sessionStorage.getItem("pm_editing");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });
  const effectiveEditing = editing ?? editingFallback;
  const isEditing = !!effectiveEditing?.postId;

  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const [text, setText] = useState(effectiveEditing?.text || "");
  const [modo, setModo] = useState(effectiveEditing?.attach?.modo || "");
  const [categoria, setCategoria] = useState(effectiveEditing?.attach?.categoria || "");
  const [previewItens, setPreviewItens] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [hasSpoiler, setHasSpoiler] = useState(!!effectiveEditing?.hasSpoiler);

  const [openMenu, setOpenMenu] = useState(null);
  const [menuMeta, setMenuMeta] = useState({ dir: "down", max: 260 });
  const modalRef = useRef(null);
  const modoRef = useRef(null);
  const categoriaRef = useRef(null);

  // estado do popup
  const [popup, setPopup] = useState({
    open: false,
    tipo: "info",
    mensagem: "",
    onConfirm: null,
  });

  const textareaRef = useRef(null);
  const { marking, start, finish, wrapSelection, unwrapSelection } = useSpoilerMarker(textareaRef, text, setText);

  const carouselRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    (async () => {
      try { setCfg(await getTimelineConfig()); }
      finally { setLoading(false); }
    })();
  }, []);

  // atualiza quando receber effectiveEditing
  useEffect(() => {
    if (!isEditing) return;
    setText(effectiveEditing?.text || "");
    setHasSpoiler(!!effectiveEditing?.hasSpoiler);
    setModo(effectiveEditing?.attach?.modo || "");
    setCategoria(effectiveEditing?.attach?.categoria || "");
    const itens = Array.isArray(effectiveEditing?.attach?.itens) ? effectiveEditing.attach.itens : [];
    const prep = itens
      .filter(Boolean)
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
      .map((p) => ({
        titulo: p.titulo,
        filme: p.filme,
        pais: p.pais,
        imagem: p.imagem || PLACEHOLDER_IMG,
        ordem: p.ordem,
        acertou: isTrue(p.acertou) ? true : isFalse(p.acertou) ? false : undefined,
      }));
    setPreviewItens(prep);
  }, [isEditing, effectiveEditing]);

  useEffect(() => {
    return () => {
      try { sessionStorage.removeItem("pm_editing"); } catch { }
    };
  }, []);

  // fecha dropdowns clicando fora / ESC
  useEffect(() => {
    const onDown = (e) => {
      if (!modalRef.current) return;
      if (!modalRef.current.contains(e.target)) setOpenMenu(null);
    };
    const onKey = (e) => e.key === "Escape" && setOpenMenu(null);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  // fecha dropdowns ao rolar o modal
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const onScroll = () => openMenu && setOpenMenu(null);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [openMenu]);

  // seta de overflow do carrossel
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const check = () => setHasOverflow(el.scrollWidth > el.clientWidth + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [previewItens]);

  const modosDisponiveis = useMemo(() => getModosDisponiveis(snapByModo), [snapByModo]);
  const categoriasDisponiveis = useMemo(
    () => getCategoriasDisponiveisOrdenadas(modo, snapByModo),
    [modo, snapByModo]
  );

  // monta preview quando seleciona modo/categoria
  useEffect(() => {
    if (!modo || !categoria) {
      if (!isEditing) setPreviewItens([]);
      return;
    }
    if (isEditing && effectiveEditing?.attach?.modo === modo && effectiveEditing?.attach?.categoria === categoria) {
      return;
    }
    const posicoes = Object.values(snapByModo?.[modo]?.[categoria] || {})
      .filter((p) => p && typeof p === "object" && (p.titulo || "").trim() !== "")
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
      .map((p) => ({
        titulo: p.titulo,
        filme: p.filme,
        pais: p.pais,
        imagem: p.imagem || PLACEHOLDER_IMG,
        ordem: p.ordem,
        acertou: isTrue(p.acertou) ? true : isFalse(p.acertou) ? false : undefined,
      }));
    setPreviewItens(posicoes);
  }, [modo, categoria, snapByModo, isEditing, effectiveEditing]);

  // calcula direção/altura do menu
  function toggleMenu(which) {
    setOpenMenu((cur) => {
      const next = cur === which ? null : which;

      if (cur !== which && which) {
        const modalEl = modalRef.current;
        const selectEl = which === "modo" ? modoRef.current : categoriaRef.current;
        if (modalEl && selectEl) {
          const rModal = modalEl.getBoundingClientRect();
          const rSel = selectEl.getBoundingClientRect();
          const spaceBelow = Math.max(0, rModal.bottom - rSel.bottom - 8);
          const spaceAbove = Math.max(0, rSel.top - rModal.top - 8);
          const wantDown = spaceBelow >= 220 || spaceBelow >= spaceAbove;
          const maxH = Math.max(120, Math.floor(wantDown ? spaceBelow : spaceAbove));
          setMenuMeta({ dir: wantDown ? "down" : "up", max: maxH });
        }
      }
      return next;
    });
  }

  function dispatchToTimeline(payload) {
    try { window.dispatchEvent(new CustomEvent("tl:post-done", { detail: payload })); } catch { }
    onDone?.(payload);
  }

  async function handlePublish() {
    if (!currentUser?.uid) return;

    const hasInline = textHasInlineSpoiler(text);
    const finalHasSpoiler = hasInline || hasSpoiler;

    if (isEditing) {
      if (!text && !effectiveEditing?.attach) {
        setPopup({ open: true, tipo: "info", mensagem: "Escreva algo ou mantenha um anexo.", onConfirm: null });
        return;
      }
      setSubmitting(true);
      try {
        await editPost({ postId: effectiveEditing.postId, newText: text || "", hasSpoiler: finalHasSpoiler });
        const payload = {
          type: "edit",
          postId: effectiveEditing.postId,
          newText: text || "",
          hasSpoiler: finalHasSpoiler,
          attach: effectiveEditing.attach ?? null,
        };
        dispatchToTimeline(payload);
        try { sessionStorage.removeItem("pm_editing"); } catch { }
        onClose?.();
      } catch (e) {
        console.error(e);
        setPopup({ open: true, tipo: "info", mensagem: e.message || "Não foi possível salvar a edição.", onConfirm: null });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!text && previewItens.length === 0) {
      setPopup({ open: true, tipo: "info", mensagem: "Escreva algo ou selecione uma aposta para anexar.", onConfirm: null });
      return;
    }

    setSubmitting(true);
    try {
      const attach =
        modo && categoria && previewItens.length
          ? {
            modo,
            categoria,
            itens: previewItens.map((p, idx) => ({
              titulo: p.titulo || "",
              filme: p.filme ?? null,
              pais: p.pais ?? null,
              imagem: p.imagem || PLACEHOLDER_IMG,
              ordem: typeof p.ordem === "number" ? p.ordem : idx + 1,
              acertou: isTrue(p.acertou) ? true : isFalse(p.acertou) ? false : null,
            })),
          }
          : null;

      const res = await createPost({
        author: {
          uid: currentUser.uid,
          name: currentUser.name,
          avatar: currentUser.avatar,
          especialista: !!currentUser.especialista,
        },
        text,
        attach,
        postingMode: cfg?.postingMode || "auto",
        hasSpoiler: finalHasSpoiler,
      });

      const postLite = {
        postId: res.postId,
        authorUid: currentUser.uid,
        authorName: currentUser.name || "Usuário",
        authorAvatar: currentUser.avatar || "",
        especialista: !!currentUser.especialista,
        text: text || "",
        attach,
        hasSpoiler: finalHasSpoiler,
        likesCount: 0,
        repliesCount: 0,
        status: res.status,
        createdAtMs: Date.now(),
        editedAtMs: null,
      };

      dispatchToTimeline({ type: "create", postLite });
      onClose?.();
    } catch (e) {
      console.error(e);
      setPopup({ open: true, tipo: "info", mensagem: e.message || "Não foi possível publicar.", onConfirm: null });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="pm-modal">
      <div className="pm-inner" ref={modalRef}>
        <header className="pm-head">
          <h2>{isEditing ? "EDITAR POST" : "POSTAR"}</h2>
          {!isEditing && <p className="pm-warning"></p>}
        </header>

        <section className="pm-textarea">
          <textarea
            ref={textareaRef}
            className={marking ? "spoiler-marking" : ""}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Faça o seu comentário aqui..."
            maxLength={MAX_TEXT_CHARS}
          />
          <div className="pm-counter">{text.length}/{MAX_TEXT_CHARS}</div>
          <div className="pm-row">
            <label className="pm-check">
              <input
                type="checkbox"
                checked={hasSpoiler}
                onChange={(e) => setHasSpoiler(e.target.checked)}
              />
              <span>Este texto contém <strong>spoilers</strong> (para esconder tudo)</span>
            </label>

            <div className="pm-marker">
              {!marking ? (
                <button type="button" className="pm-marker-btn" onClick={start}>
                  Marcador de Spoiler
                </button>
              ) : (
                <div className="pm-marker-on">
                  <span className="pm-marker-hint">use o cursor para selecionar os trechos e clique “Aplicar à seleção”</span>
                  <button type="button" className="pm-marker-finish" onClick={finish}>
                    Terminei de Marcar
                  </button>
                  <div className="pm-marker-tools">
                    <button type="button" onClick={wrapSelection} title="Aplicar à seleção (Ctrl/Cmd+M)">Aplicar à seleção</button>
                    <button type="button" onClick={unwrapSelection} title="Remover tags da seleção">Remover da seleção</button>
                  </div>
                </div>
              )}
            </div>
          </div>


        </section>

        <section className={`pm-attach ${isEditing ? "pm-attach--readonly" : ""}`}>
          <h3>
            {isEditing ? "Aposta anexada" : "Quer também anexar a sua aposta?"}
            {!isEditing && <small> (Apenas um por post)</small>}
          </h3>

          {/* SELECT DE MODOS */}
          <div
            className={`pm-select ${(!modosDisponiveis.length || isEditing) ? "disabled" : ""}`}
            ref={modoRef}
          >
            <button
              className="pm-select-btn"
              disabled={!modosDisponiveis.length || isEditing}
              onClick={() => toggleMenu("modo")}
            >
              <span>APOSTAS</span>
              <img src={ICONS.arrowDropdown} alt="" />
            </button>

            <div
              className={`pm-select-menu ${openMenu === "modo" ? "open" : ""} ${menuMeta.dir === "up" ? "open-up" : ""}`}
              style={{
                maxHeight: menuMeta.max,
                top: menuMeta.dir === "down" ? "calc(100% + 10px)" : "auto",
                bottom: menuMeta.dir === "up" ? "calc(100% + 10px)" : "auto",
              }}
            >
              {modosDisponiveis.map((m) => (
                <button
                  key={m}
                  onClick={() => { setModo(m); setCategoria(""); setOpenMenu(null); }}
                  className={m === modo ? "active" : ""}
                >
                  {MODO_LABELS[m] || m}
                </button>
              ))}
            </div>

            {!!modo && (
              <div className="pm-select-chosen">{MODO_LABELS[modo] || modo}</div>
            )}
          </div>

          {/* SELECT DE CATEGORIAS */}
          <div
            className={`pm-select ${(!modo || !categoriasDisponiveis.length || isEditing) ? "disabled" : ""}`}
            ref={categoriaRef}
          >
            <button
              className="pm-select-btn burgundy"
              disabled={!modo || !categoriasDisponiveis.length || isEditing}
              onClick={() => toggleMenu("categoria")}
            >
              <span>CATEGORIAS</span>
              <img src={ICONS.arrowDropdown} alt="" />
            </button>

            <div
              className={`pm-select-menu ${openMenu === "categoria" ? "open" : ""} ${menuMeta.dir === "up" ? "open-up" : ""}`}
              style={{
                maxHeight: menuMeta.max,
                top: menuMeta.dir === "down" ? "calc(100% + 10px)" : "auto",
                bottom: menuMeta.dir === "up" ? "calc(100% + 10px)" : "auto",
              }}
            >
              {categoriasDisponiveis.map((slug) => (
                <button
                  key={slug}
                  onClick={() => { setCategoria(slug); setOpenMenu(null); }}
                  className={slug === categoria ? "active" : ""}
                >
                  {labelCategoria(slug)}
                </button>
              ))}
            </div>

            {!!categoria && (
              <div className="pm-select-chosen">{labelCategoria(categoria)}</div>
            )}
          </div>

          {(modo && categoria) || (isEditing && effectiveEditing?.attach) ? (
            <div className="pm-preview-head">
              <div className="pm-preview-title">
                APOSTAS – {MODO_LABELS[modo] || MODO_LABELS[effectiveEditing?.attach?.modo] || modo || effectiveEditing?.attach?.modo} • {labelCategoria(categoria || effectiveEditing?.attach?.categoria)}
              </div>
              <div className="pm-dots" aria-hidden="true">············································</div>
            </div>
          ) : null}

          <div className="pm-carousel-wrap">
            <div className="pm-carousel" ref={carouselRef}>
              {previewItens.map((item, idx) => {
                const pos = idx + 1;
                const acertou = isTrue(item.acertou);
                const errou = isFalse(item.acertou);
                const sub = item.filme || item.pais || "";
                const img = item.imagem || PLACEHOLDER_IMG;

                return (
                  <div className={`pm-card ${errou ? "miss" : ""}`} key={`${item.titulo}-${idx}`}>
                    <div className="pm-thumb-wrap">
                      <div className="pm-thumb-mask">
                        <img
                          src={img}
                          alt={item.titulo || ""}
                          loading="lazy"
                          onError={(e) => {
                            if (e.currentTarget.src !== PLACEHOLDER_IMG) {
                              e.currentTarget.src = PLACEHOLDER_IMG;
                            }
                          }}
                        />
                      </div>
                      {acertou && <span className="pm-hit">ACERTEI</span>}
                      <span className={`pm-badge ${classePos(pos)}`}>{pos}º</span>
                    </div>

                    <div className="pm-meta">
                      <strong className="pm-title">{item.titulo}</strong>
                      {!!sub && <div className="pm-sub">{sub}</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasOverflow && (
              <div className="pm-overflow-hint" aria-hidden="true">
                <img src={ICONS.arrowOverflow} alt="" />
              </div>
            )}
          </div>
        </section>

        <footer className="pm-bottom">
          <div className="pm-buttons">
            <button className="pm-publish" onClick={handlePublish} disabled={submitting || loading}>
              {submitting ? (isEditing ? "Salvando…" : "Publicando…") : (isEditing ? "SALVAR" : "PUBLICAR")}
            </button>
            <button
              className="pm-close"
              onClick={() => {
                try { sessionStorage.removeItem("pm_editing"); } catch { }
                onClose?.();
              }}
            >
              FECHAR
            </button>
          </div>
          {!isEditing && (
            <p className="pm-note">
              Atenção, se sua aposta recente ainda não apareceu. Espere algumas horas
              ou dê um <strong>hard refresh</strong> no seu navegador..
            </p>
          )}
        </footer>
      </div>

      <button
        className="pm-backdrop"
        onClick={() => { try { sessionStorage.removeItem("pm_editing"); } catch { }; onClose?.(); }}
        aria-label="Fechar postar"
      />

      {/* Popup do PostarModal */}
      <XPopup
        open={popup.open}
        tipo={popup.tipo}
        mensagem={popup.mensagem}
        onClose={() => setPopup({ open: false, tipo: "info", mensagem: "", onConfirm: null })}
        onConfirm={popup.onConfirm}
      />
    </div>
  );
}
